import { useRoute, useRouter } from 'vue-router';
import { useAuthentication, useProduct, useEnvironment, useRedirectManager, AuthState } from 'ihunter-pathfinder-components';
import { useI18n } from 'vue-i18n';

export function useRouteManager() {
    const router = useRouter();
    const route = useRoute();
    const { product, ProductType } = useProduct();
    const { user, embeddedResetPasswordUrl, signInWithExistingSession } = useAuthentication();
    const { config } = useEnvironment();
    const { locale } = useI18n();
    const { redirectToWeb } = useRedirectManager();

    const redirectToRoute = (routeName, queryParams = {}) => {
        const params = { name: routeName, query: queryParams };
        if (product.value !== ProductType.Accounts && !params.query.continue) {
            params.query.continue = product.value.name;
        }
        console.log(`Redirecting to ${routeName}`, params);
        router.push(params);
    }

    const redirectToLogin = (queryParams) => {
        redirectToRoute('Login', queryParams);
    }

    const redirectToLoginWithAuthState = (authState, authEmail, continueToAccounts) => {
        console.log(`redirectToLoginWithAuthState ${authState}, ${authEmail}`);
        const queryParams = { authState, authEmail, continueToAccounts };
        redirectToLogin(queryParams);
    }

    const redirectToHome = (queryParams) => {
        if (user.value) {
            if (route.query.continueToAccounts) {
                const continueToAccounts = atob(route.query.continueToAccounts);
                console.log(`redirectToHome continueToAccounts`, continueToAccounts);
                window.location = continueToAccounts;
            } else {
                redirectToRoute('Home', queryParams);
            }
        }
    }

    const redirectToHomeOrLogin = () => {
        if (user.value) {
            redirectToHome();
        } else {
            redirectToLoginWithAuthState(AuthState.SignIn, user.value?.email, route.query.continueToAccounts);
        }
    }

    const redirectToHomeOrContinue = async (queryParams) => {
        if (user.value) {
            if (product.value !== ProductType.Accounts) {
                try {
                    console.log(`User ${user.value.uid} authenticated, validating session before redirecting to ${product.value.name}...`);
                    await signInWithExistingSession();
                    if (user.value.isNewUser) {
                        console.log(`Session is valid for new user ${user.value.uid}`);
                        redirectToHome(queryParams);
                    } else {
                        console.log(`Session is valid, redirecting to ${product.value.name}`);
                        window.location = product.value.url;
                    }
                    return;
                } catch (error) {
                    console.info(`User must login before they can be redirected to ${product.value.name}`);
                }
            } else {
                redirectToHome(queryParams);
            }
        }
    };

    const redirectToResetPassword = () => {
        const continueToAccounts = btoa(window.location.href);
        if (embeddedResetPasswordUrl.value) {
            window.location = `${embeddedResetPasswordUrl.value}&continueToAccounts=${continueToAccounts}`;
        } else {
            redirectToLoginWithAuthState(AuthState.RequestResetPassword, user.value?.email, continueToAccounts);
        }
    }

    const redirectToWebOrLogin = async () => {
        console.log(`redirectToWebOrLogin`);
        if (user.value) {
            redirectToWeb();
        } else {
            const queryParams = { continue: 'web' };
            redirectToLogin(queryParams);
        }
    }

    // Retrieve temp query params and delete them from the url.
    // Enables passing of query params between routes without impacting the user-visible url.
    const getTempQueryParams = (names) => {
        const query = Object.assign({}, route.query);
        const params = {};
        for (const name of names) {
            if (query[name]) {
                params[name] = query[name];

            }
            delete query[name];
        }
        router.replace({ query });
        return params;
    }

    async function setRouteLocale(routeLocale) {
        locale.value = routeLocale;
        router.push({ params: { locale: routeLocale } });
        console.log(`Locale set to ${routeLocale}`);
    }

    return {
        redirectToLogin,
        redirectToLoginWithAuthState,
        redirectToHomeOrLogin,
        redirectToHomeOrContinue,
        redirectToResetPassword,
        redirectToWebOrLogin,
        getTempQueryParams,
        setRouteLocale
    };

}
